import { z } from 'zod';
import type { Action } from './permissions.js';
import type { InterventionStatus } from './schemas/common.js';
import { addDaysToKey, daysBetweenKeys, localDateKey, startOfLocalDay } from './zoned.js';

/**
 * Vague 7 : **indicateurs de priorité** d'un dossier (`InterventionSummary.daysOnSite`, `urgency`,
 * `nextAction`) et tri `priority` de `interventions.list`. Calculés par le serveur (L1) avec ces fonctions,
 * seule définition des règles ; l'interface ne fait que les afficher.
 */

/**
 * Urgence, première règle qui s'applique :
 * 1. `late` : dossier ni prêt ni clôturé, et (créneau d'agenda terminé **ou** plus de `lateAfterDays` jours à
 *    l'atelier, réglage du garage, 7 par défaut) ;
 * 2. `waiting_customer` : en attente de décision (`awaiting_decision`) ou prêt, en attente du client (`ready`) ;
 * 3. `waiting_parts` : en attente de pièces (`awaiting_parts`) ;
 * 4. `normal` : sinon (clôturé compris).
 */
export const Urgency = z.enum(['late', 'waiting_customer', 'waiting_parts', 'normal']);
export type Urgency = z.infer<typeof Urgency>;
export const URGENCY_LABELS: Record<Urgency, string> = {
  late: 'En retard',
  waiting_customer: 'Attente client',
  waiting_parts: 'Attente pièces',
  normal: 'Normal',
};
/** Rang du tri `priority` (plus petit = plus haut). */
export const URGENCY_RANK: Record<Urgency, number> = { late: 0, waiting_customer: 1, waiting_parts: 2, normal: 3 };

/**
 * Action suivante proposée au membre (bouton principal de la fiche), selon l'état **et ses droits** ;
 * première règle qui s'applique :
 * 1. `answer_question` : question du client non traitée et `question.answer` ;
 * 2. `send_complement` : travaux complémentaires à valider et `complement.send`, ou modification demandée et
 *    `complement.draft` (réviser puis renvoyer) ;
 * 3. `start_work` : statut `received` et `intervention.update` ;
 * 4. `mark_ready` : statut `in_progress` et `intervention.ready` ;
 * 5. `release` : statut `ready` et `intervention.release` ;
 * 6. `none`.
 * `check_in` : réservé aux interventions planifiées (agenda, arrivées), avec `intervention.create`.
 */
export const NextAction = z.enum(['start_work', 'send_complement', 'mark_ready', 'release', 'answer_question', 'check_in', 'none']);
export type NextAction = z.infer<typeof NextAction>;
export const NEXT_ACTION_LABELS: Record<NextAction, string> = {
  start_work: 'Démarrer les travaux',
  send_complement: 'Envoyer les travaux complémentaires',
  mark_ready: 'Marquer prêt',
  release: 'Restituer le véhicule',
  answer_question: 'Répondre au client',
  check_in: 'Faire entrer le véhicule',
  none: 'Aucune action',
};

/** Seuil par défaut du retard (jours à l'atelier), réglage `lateAfterDays` du garage. */
export const DEFAULT_LATE_AFTER_DAYS = 7;

/**
 * Jours à l'atelier : jours calendaires **locaux** (fuseau du garage) entre l'entrée et aujourd'hui, ou la
 * sortie si le véhicule est sorti. 0 le jour de l'entrée.
 */
export function daysOnSite(
  input: { receivedAt: Date | string; releasedAt?: Date | string | null },
  timeZone: string,
  now: Date = new Date(),
): number {
  const from = localDateKey(new Date(input.receivedAt), timeZone);
  const to = localDateKey(input.releasedAt ? new Date(input.releasedAt) : now, timeZone);
  return Math.max(0, daysBetweenKeys(from, to));
}

export function interventionUrgency(
  input: {
    status: InterventionStatus;
    daysOnSite: number;
    /** Fin du créneau d'agenda (début + durée), ou `null`. */
    agendaEndsAt?: Date | string | null;
    lateAfterDays?: number;
  },
  now: Date = new Date(),
): Urgency {
  const open = input.status !== 'ready' && input.status !== 'closed';
  const threshold = input.lateAfterDays ?? DEFAULT_LATE_AFTER_DAYS;
  const agendaOver = input.agendaEndsAt ? new Date(input.agendaEndsAt).getTime() < now.getTime() : false;
  if (open && (agendaOver || input.daysOnSite > threshold)) return 'late';
  if (input.status === 'awaiting_decision' || input.status === 'ready') return 'waiting_customer';
  if (input.status === 'awaiting_parts') return 'waiting_parts';
  return 'normal';
}

export function nextActionFor(
  input: { status: InterventionStatus; openQuestions: number; complementsToValidate: number; changeRequests?: number },
  can: (action: Action) => boolean,
): NextAction {
  if (input.status === 'closed') return 'none';
  if (input.openQuestions > 0 && can('question.answer')) return 'answer_question';
  if (
    (input.complementsToValidate > 0 && can('complement.send')) ||
    ((input.changeRequests ?? 0) > 0 && can('complement.draft'))
  ) {
    return 'send_complement';
  }
  if (input.status === 'received' && can('intervention.update')) return 'start_work';
  if (input.status === 'in_progress' && can('intervention.ready')) return 'mark_ready';
  if (input.status === 'ready' && can('intervention.release')) return 'release';
  return 'none';
}

/**
 * Tri `priority` : urgence (`URGENCY_RANK`), puis, à urgence égale « attente client », le dossier **en
 * attente de décision** avant le véhicule **prêt** qui attend seulement d'être repris (retour du porteur,
 * 02/10/2026 ; `WAITING_CUSTOMER_RANK`), puis jours à l'atelier (le plus ancien d'abord), puis date d'entrée
 * (la plus ancienne d'abord), puis identifiant (ordre stable). Seule définition : API, maquettes, interface.
 * `status` absent : pas de sous-rang. 03/10/2026 : un dossier **épinglé** par le membre (`pinned`) passe juste
 * après les dossiers en retard, avant tous les autres (`priorityTier`).
 */
export const WAITING_CUSTOMER_RANK: Partial<Record<InterventionStatus, number>> = { awaiting_decision: 0, ready: 1 };

export interface PriorityItem {
  urgency: Urgency;
  daysOnSite: number;
  receivedAt: string;
  id: string;
  status?: InterventionStatus;
  /** 03/10/2026 : épinglé par le membre courant. */
  pinned?: boolean;
}

/** Palier du tri : 0 en retard, 1 épinglé (hors retard), 2 les autres. */
export function priorityTier(i: Pick<PriorityItem, 'urgency' | 'pinned'>): number {
  return i.urgency === 'late' ? 0 : i.pinned ? 1 : 2;
}

function waitingCustomerRank(i: PriorityItem): number {
  return i.urgency === 'waiting_customer' && i.status ? (WAITING_CUSTOMER_RANK[i.status] ?? 0) : 0;
}

export function comparePriority(a: PriorityItem, b: PriorityItem): number {
  return (
    priorityTier(a) - priorityTier(b) ||
    URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency] ||
    waitingCustomerRank(a) - waitingCustomerRank(b) ||
    b.daysOnSite - a.daysOnSite ||
    a.receivedAt.localeCompare(b.receivedAt) ||
    a.id.localeCompare(b.id)
  );
}

// ─── Dernière action et dossiers sans action (02/10/2026) ────────────────────

/**
 * Seuil par défaut du réglage `idleAfterDays` du garage (1 à 60) : un dossier non clôturé est « sans action »
 * quand `idleDays >= idleAfterDays`.
 */
export const DEFAULT_IDLE_AFTER_DAYS = 3;
export const IDLE_AFTER_DAYS_MIN = 1;
export const IDLE_AFTER_DAYS_MAX = 60;

/**
 * Jours sans action : jours calendaires **locaux** (fuseau du garage) entre la dernière action
 * (`lastActivityAt`) et aujourd'hui. 0 le jour même ; jamais négatif.
 */
export function idleDays(lastActivityAt: Date | string, timeZone: string, now: Date = new Date()): number {
  const from = localDateKey(new Date(lastActivityAt), timeZone);
  const to = localDateKey(now, timeZone);
  return Math.max(0, daysBetweenKeys(from, to));
}

/** Indicateur `idle` : dossier non clôturé et `idleDays >= idleAfterDays` (défaut `DEFAULT_IDLE_AFTER_DAYS`). */
export function isIdle(input: { status: InterventionStatus; idleDays: number; idleAfterDays?: number }): boolean {
  return input.status !== 'closed' && input.idleDays >= (input.idleAfterDays ?? DEFAULT_IDLE_AFTER_DAYS);
}

/**
 * Borne équivalente à `idleDays(t) >= idleAfterDays` pour un filtre en base : `t < idleCutoff(...)`, début du
 * jour local « aujourd'hui − idleAfterDays + 1 » converti en UTC (changements d'heure compris).
 */
export function idleCutoff(timeZone: string, idleAfterDays: number, now: Date = new Date()): Date {
  return startOfLocalDay(addDaysToKey(localDateKey(now, timeZone), 1 - idleAfterDays), timeZone);
}
